import * as yup from 'yup';
import { RendererType } from '../data-models/field-definition';

export const FilterType = {
  STRING: 'string',
  NUMBER: 'number',
  DATE: 'date',
  BOOLEAN: 'boolean',
  ENUM: 'enum',
} as const;

export type FilterType = (typeof FilterType)[keyof typeof FilterType];

export function filterableColumnFields() {
  return {
    columnName: yup.string().required(),
    displayName: yup.string().required(),
    filterType: yup.string().oneOf(Object.values(FilterType)).required(),
    enumValues: yup.array().of(yup.string()).nullable(),
  };
}

export function filterableColumnSchema() {
  return yup.object(filterableColumnFields());
}

export type FilterableColumn = yup.InferType<ReturnType<typeof filterableColumnSchema>>;

export function downloadConfigFields() {
  return {
    id: yup.number().required(),
    tableName: yup.string().required(),
    displayName: yup.string().required(),
    filterableColumns: yup.array().of(filterableColumnSchema()).required().default([]),
    enabled: yup.boolean().required(),
    excludedColumns: yup.array().of(yup.string()).nullable(),
    description: yup.string().nullable(),
    createdAt: yup.string().nullable(),
    updatedAt: yup.string().nullable(),
  };
}

export function downloadConfigSchema() {
  return yup.object(downloadConfigFields());
}

export type DownloadConfig = yup.InferType<ReturnType<typeof downloadConfigSchema>>;

export const ExportFormat = {
  CSV: 'csv',
  EXCEL: 'excel',
} as const;

export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

export function filterValueFields() {
  return {
    eq: yup.mixed<string | number | boolean>().nullable().label('Equals'),
    neq: yup.mixed<string | number | boolean>().nullable().label('Not Equals'),
    gt: yup.mixed<string | number>().nullable().label('Greater Than'),
    gte: yup.mixed<string | number>().nullable().label('Greater or Equal'),
    lt: yup.mixed<string | number>().nullable().label('Less Than'),
    lte: yup.mixed<string | number>().nullable().label('Less or Equal'),
    like: yup.string().nullable(),
    in: yup.array().of(yup.mixed<string | number>()).nullable(),
    notIn: yup.array().of(yup.mixed<string | number>()).nullable(),
    isNull: yup.boolean().nullable(),
  };
}

export function filterValueSchema() {
  return yup.object(filterValueFields());
}
export type FilterValue = yup.InferType<ReturnType<typeof filterValueSchema>>;

export function downloadRequestFields() {
  return {
    tableName: yup.string().required().default(''),
    format: yup
      .string()
      .oneOf(Object.values(ExportFormat))
      .required()
      .default(ExportFormat.CSV)
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: Object.values(ExportFormat).map((v) => ({
            value: v,
            displayName: v.toUpperCase(),
          })),
        },
        disableClearable: true,
      }),
    filters: yup.object().nullable(),
    columns: yup.array().of(yup.string().required()).nullable(),
    sortBy: yup.string().nullable().formMeta({
      renderer: RendererType.singleSelect,
    }),
    sortOrder: yup
      .string()
      .oneOf(['ASC', 'DESC'])
      .default('ASC')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: [
            { value: 'ASC', displayName: 'Ascending' },
            { value: 'DESC', displayName: 'Descending' },
          ],
        },
        disableClearable: true,
      })
      .nullable(),
    limit: yup
      .number()
      .nullable()
      .min(1)
      .max(100000)
      .formMeta({
        placeholder: 'Max rows to export (max: 100,000)',
      })
      .default(10000),
    offset: yup
      .number()
      .nullable()
      .min(0)
      .formMeta({
        placeholder: 'Rows to skip',
      })
      .default(0),
  };
}

export function downloadRequestSchema() {
  return yup.object(downloadRequestFields());
}

export type DownloadRequest = Omit<
  yup.InferType<ReturnType<typeof downloadRequestSchema>>,
  'filters' | 'sortOrder'
> & {
  filters?: Record<string, string | number | boolean | FilterValue>;
  sortOrder?: 'ASC' | 'DESC' | null;
};

export function createDownloadConfigFields() {
  return {
    tableName: yup.string().required(),
    displayName: yup.string().required(),
    filterableColumns: yup.array().of(filterableColumnSchema()).required().default([]),
    enabled: yup.boolean().nullable().default(true),
    excludedColumns: yup.array().of(yup.string()).nullable().default([]),
    description: yup.string().nullable(),
  };
}

export function createDownloadConfigSchema() {
  return yup.object(createDownloadConfigFields());
}

export type CreateDownloadConfig = yup.InferType<ReturnType<typeof createDownloadConfigSchema>>;

export function updateDownloadConfigFields() {
  return {
    displayName: yup.string().nullable(),
    filterableColumns: yup.array().of(filterableColumnSchema()).nullable().default([]),
    enabled: yup.boolean().nullable(),
    excludedColumns: yup.array().of(yup.string()).nullable().default([]),
    description: yup.string().nullable(),
  };
}

export function updateDownloadConfigSchema() {
  return yup.object(updateDownloadConfigFields());
}

export type UpdateDownloadConfig = yup.InferType<ReturnType<typeof updateDownloadConfigSchema>>;
